/**
 * Vertically concatenate multiple CCCD/GPLX card images into a single
 * JPEG File. Sent as ONE mistral-ocr-latest call instead of N — mistral
 * bills per page, so 2 images concat'd = 1 page = 50% cost saved. Also
 * halves the network round-trips on mobile, which matters far more than
 * the pennies of savings when the operator is waiting on 5G.
 *
 * Layout: all images resized to the SAME width (max of any input), then
 * stacked top-to-bottom with a thin black separator line. This preserves
 * card aspect ratios and gives the OCR engine clear visual delimiters
 * between pages — it treats them as a 2-page document.
 */
export const concatImagesVertically = async (files: File[], maxWidth = 1600, jpegQuality = 0.85): Promise<File | null> => {
  if (!files || files.length === 0) return null;
  if (files.length === 1) return files[0];

  try {
    // Load every file into an HTMLImageElement (concurrent decode).
    const images = await Promise.all(
      files.map((f) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
          const url = URL.createObjectURL(f);
          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('decode'));
          };
          img.src = url;
        }),
      ),
    );

    // Common width — pick the smaller of (largest input width, maxWidth)
    // so a huge photo doesn't blow up the canvas memory.
    const targetWidth = Math.min(
      maxWidth,
      Math.max(...images.map((i) => i.naturalWidth || i.width || 0)),
    );

    // Compute scaled heights to match targetWidth for every image.
    const scaled = images.map((img) => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      if (!w || !h) return { img, w: 0, h: 0 };
      const scale = targetWidth / w;
      return { img, w: Math.round(w * scale), h: Math.round(h * scale) };
    });

    const separator = 8; // px — visible black band between pages
    const totalHeight = scaled.reduce((sum, s) => sum + s.h, 0) + separator * (scaled.length - 1);

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = totalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return files[0];

    // White background fills any width gap (shouldn't happen, all same
    // width) + separator band color.
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let y = 0;
    for (const s of scaled) {
      if (s.w && s.h) {
        ctx.drawImage(s.img, 0, y, s.w, s.h);
      }
      y += s.h + separator;
    }

    const blob: Blob | null = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), 'image/jpeg', jpegQuality),
    );
    if (!blob) return files[0];

    const baseName = files[0].name.replace(/\.[^.]+$/, '') || 'stacked';
    return new File([blob], `${baseName}-stacked.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    // On any failure, fall back to the first image — caller will still
    // get a reasonable result; we just lose the concat optimisation.
    return files[0];
  }
};
